import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { filter, flow } from 'lodash'
import * as blueprint from '@adc/blueprint'
import Header from '../header'
import {
  transformGraphNotification,
  markAsRead,
  useApplicationSessionInformation,
} from './applicationSessionInformation'
import { useApplicationContext } from './context'
import {
  fetchApplicationSessionInformation,
  markAllNotificationsAsRead,
} from './queries/applicationSessionInformation'
import { useApplicationModals } from './modals/'
import { AUTH_TYPES } from '../authToggle/constants'

const ApplicationHeaderWrapper = React.memo(
  ({ headerProps = { ...Header.defaultProps }, featureFlags = {} }) => {
    useApplicationSessionInformation()

    const modals = useApplicationModals()
    const { logger, user, notifications, setNotifications } =
      useApplicationContext()

    const notificationsRef = useRef()
    const [isFetching, setIsFetching] = useState(false)
    const [bellNotificationsOpen, setBellNotificationsOpen] = useState(false)
    notificationsRef.current = notifications

    // Channel-generic PRP unseen-update indicator (orange dot). The hook reads the
    // LD flag, fetches, and computes; here we only name the category. Works across
    // every app that renders through AuctionApplication.
    const {
      showIndicator: showDealsForYouIndicator,
      updatedDate: dealsForYouUpdatedDate,
    } = blueprint.user.hooks.usePrpCategoryIndicator({
      category: blueprint.user.prpIndicator.DEALS_FOR_YOU_CATEGORY,
      featureFlags,
      isLoggedIn: user?.logged_in ?? false,
    })

    const fetchNotifications = React.useCallback(async () => {
      // the hook above will fetch the first page. Page 0.
      if (
        !isFetching &&
        notificationsRef.current.pagination.page + 1 <
          notificationsRef.current.pagination.totalPages
      ) {
        const newPage = notificationsRef.current.pagination.page + 1
        return await fetchApplicationSessionInformation({
          page: newPage,
          take: notificationsRef.current.pagination.take,
          channel: 'WEB',
          displayed: true,
        })
          .then(res => {
            setNotifications(notifications => ({
              ...notifications,
              pagination: { ...notifications.pagination, page: newPage },
              data: [
                ...notifications.data,
                ...(res.notifications?.content ?? []).map(
                  flow(transformGraphNotification, markAsRead)
                ),
              ],
            }))
            return setIsFetching(false)
          })
          .catch(e => {
            logger.error('appHeader: fetchNotifications error')
          })
      }
      return Promise.resolve({})
    }, [isFetching, setNotifications, logger])

    // TODO: Wrap in state wrapper so we don't re-render here...
    const unreadNotifications = filter(notifications.data, { read: false })

    const markNotificationsRead = React.useCallback(() => {
      const currentNotifications = notificationsRef.current?.data ?? []
      const allRead = currentNotifications.every(n => n.read === true)

      if (allRead) {
        return
      }

      // locally update the bellNotification array to mark all as read
      // this will remove the number and red circle on the bell
      setNotifications(prevNotifications => {
        const readNotifications = (prevNotifications.data ?? []).map(n => {
          n.read = true
          return n
        })

        return {
          ...prevNotifications,
          unreadCount: 0,
          data: readNotifications,
        }
      })

      // send request to update all as read api side
      markAllNotificationsAsRead().catch(e => {
        logger.error('appHeader: failed to mark notifications as read')
      })
    }, [logger, setNotifications])

    const onClickBellNotifications = React.useCallback(() => {
      setBellNotificationsOpen(true)
      markNotificationsRead()
    }, [markNotificationsRead])

    const onCloseBellNotifications = React.useCallback(() => {
      setBellNotificationsOpen(false)
      markNotificationsRead()
    }, [markNotificationsRead])

    return (
      <Header
        onLoginClick={() =>
          modals.open('authentication', {
            authInitialActive: AUTH_TYPES.LOGIN,
          })
        }
        onSignupClick={() =>
          modals.open('authentication', {
            authInitialActive: AUTH_TYPES.SIGNUP,
          })
        }
        {...headerProps}
        user={user}
        showDealsForYouIndicator={showDealsForYouIndicator}
        dealsForYouUpdatedDate={dealsForYouUpdatedDate}
        bellNotifications={notificationsRef.current.data}
        bellNotificationsOpen={bellNotificationsOpen}
        onClickBellNotifications={onClickBellNotifications}
        onCloseBellNotifications={onCloseBellNotifications}
        onDrawerFetchMore={fetchNotifications}
        unreadCount={unreadNotifications.length}
      />
    )
  }
)

ApplicationHeaderWrapper.propTypes = {
  featureFlags: PropTypes.object,
  headerProps: PropTypes.shape({
    ...Header.propTypes,
  }),
}

export default ApplicationHeaderWrapper
